import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AlertTriangle, Download, Loader2, Droplets, CheckCircle2, ShieldCheck, Clock3, Cpu } from "lucide-react";
import { getDanaState, getDanaGeo, downloadDanaReport } from "../lib/api";
import { AlertFeed } from "./AlertFeed";
import { LeadTimeChart } from "./LeadTimeChart";
import { useT, useLang } from "../lib/i18n";

const VALENCIA = [39.43, -0.48];
const CHECKPOINTS = ["T-72", "T-48", "T-24", "T-12", "T-6", "T-3", "T-0"];
const DARK = `${process.env.REACT_APP_BACKEND_URL}/api/geo/basemap/{z}/{y}/{x}.png`;

function buildMap(container, center, zoom) {
  const map = L.map(container, { center, zoom, zoomControl: false, attributionControl: false, preferCanvas: true });
  L.tileLayer(DARK, { maxZoom: 16 }).addTo(map);
  L.control.zoom({ position: "bottomright" }).addTo(map);
  const timer = setTimeout(() => { if (map._loaded && map._mapPane) map.invalidateSize(); }, 150);
  map.on("unload", () => clearTimeout(timer));
  return map;
}

function floodLayer(fc, color) {
  return L.geoJSON(fc, {
    style: { color, weight: 1.3, fillColor: color, fillOpacity: 0.4 },
    onEachFeature: (f, layer) =>
      layer.bindPopup(`<div class="resilio-popup"><b>${f.properties?.nombre || "—"}</b><br/>${f.properties?.capa || ""}</div>`),
  });
}

const sevBadge = (sev) =>
  sev === "critical" ? "bg-red-500/15 text-red-400 border-red-500/40"
    : sev === "warning" ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
      : "bg-slate-700/40 text-slate-300 border-slate-600";

const FocusPanel = ({ f, lang }) => {
  const es = lang === "es";
  const sev = f.severity === "critical" ? (es ? "CRÍTICO" : "CRITICAL") : f.severity === "warning" ? (es ? "AVISO" : "WARNING") : (es ? "OPERATIVO" : "OPERATIONAL");
  const fc = f.kind === "forecast";
  const simTime = new Date(f.sim_time).toLocaleString(es ? "es-ES" : "en-GB", { timeZone: "Europe/Madrid", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="rounded-lg border border-indigo-500/25 bg-[#0B0F19] p-3 space-y-2.5" data-testid="dana-focus">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-mono tracking-wider text-indigo-300 min-w-0">
          <Clock3 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{f.municipio} · {simTime}</span>
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded border font-mono text-[10px] tracking-wider ${sevBadge(f.severity)}`} data-testid="dana-focus-severity">
          {fc ? (es ? "PREVISIÓN · " : "FORECAST · ") : ""}{sev}
        </span>
      </div>
      <div className="flex items-end justify-between">
        <div className="text-[10px] text-slate-400" data-testid="dana-focus-rain">
          {es ? "Lluvia 24 h" : "24 h rain"} {f.rain_24h_mm} mm · AEMET {f.observed_mm_aemet} mm est. · {es ? "Pronóstico 24 h" : "24 h forecast"} {f.forecast_24h_mm} mm · {es ? "Base" : "Baseline"} {f.baseline_mm} mm
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">{es ? "Confianza" : "Confidence"}</div>
          <div className="font-mono text-lg leading-none text-indigo-200" data-testid="dana-focus-confidence">{f.confidence != null ? `${f.confidence}%` : "—"}</div>
        </div>
      </div>
      <div className="h-1.5 rounded bg-slate-800 overflow-hidden">
        <div className="h-full bg-indigo-400 transition-all duration-300" style={{ width: `${f.confidence || 0}%` }} />
      </div>
      <div className="space-y-1">
        <div className="text-[9px] uppercase tracking-wider text-slate-500">{es ? "Fuentes confirmadas (calculadas)" : "Confirmed sources (computed)"} ({f.confirming_sources.length})</div>
        {f.confirming_sources.length === 0 && <div className="text-[11px] text-slate-500" data-testid="dana-focus-no-sources">{es ? "Ninguna fuente supera los umbrales" : "No source crosses the thresholds"}</div>}
        {f.confirming_sources.map((src) => (
          <div key={src} className="flex items-center gap-1.5 text-[11px] text-slate-200" data-testid="dana-focus-source"><CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-400" />{src}</div>
        ))}
        {f.saih_reading && f.saih_thresholds && (
          <div className="text-[10px] font-mono text-sky-300" data-testid="dana-focus-saih">
            {f.saih_reading.gauge}: {f.saih_reading.flow_m3s} m³/s ({f.saih_reading.at}) · {f.saih_reading.status.toUpperCase()} · {f.saih_reading.return_period}
            <span className="text-slate-500"> · {lang === "es" ? "umbrales oficiales CHJ" : "official CHJ thresholds"} {f.saih_thresholds.low}/{f.saih_thresholds.medium}/{f.saih_thresholds.high} m³/s</span>
            {f.saih_reading.note && (
              <div className="mt-0.5 font-sans text-amber-300/90" data-testid="dana-focus-saih-note">{f.saih_reading.note[lang] || f.saih_reading.note.es}</div>
            )}
          </div>
        )}
        {f.sovereign_verified && (
          <div className="flex items-center gap-1.5 text-[11px] text-cyan-300" data-testid="dana-focus-sovereign"><ShieldCheck className="h-3 w-3 shrink-0" />{es ? "Verificación soberana" : "Sovereign-verified"} · SNCZI {f.snczi_overlap}%</div>
        )}
      </div>
    </div>
  );
};

const MapPanel = ({ label, color }) => (
  <div className="absolute top-2 left-2 z-[1000] inline-flex items-center gap-1.5 bg-[#0B0F19]/90 backdrop-blur-md border border-slate-800 rounded px-2.5 py-1 font-mono text-[10px] tracking-wider text-slate-200">
    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
    {label}
  </div>
);

export const DanaSimulation = () => {
  const t = useT();
  const lang = useLang();
  const [checkpoint, setCheckpoint] = useState("T-72");
  const [state, setState] = useState(null);
  const [geo, setGeo] = useState(null);
  const mainRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const maps = useRef({});
  const focusMarkers = useRef([]);
  const isSplit = checkpoint === "T-0";
  const predicted = state?.checkpoint === checkpoint ? state.predicted : null;

  useEffect(() => {
    getDanaGeo().then(setGeo).catch(() => {});
  }, []);

  useEffect(() => {
    let live = true;
    getDanaState(checkpoint).then((s) => live && setState(s)).catch(() => {});
    return () => { live = false; };
  }, [checkpoint]);

  // (re)build the Leaflet map(s) for the active checkpoint with its computed prediction
  useEffect(() => {
    if (!geo || !predicted) return;
    Object.values(maps.current).forEach((m) => m && m.remove());
    maps.current = {};
    if (isSplit) {
      if (leftRef.current && rightRef.current) {
        const left = buildMap(leftRef.current, VALENCIA, 10);
        const right = buildMap(rightRef.current, VALENCIA, 10);
        floodLayer(predicted, "#EF4444").addTo(left);
        floodLayer(geo.impact, "#FB923C").addTo(right);
        maps.current = { left, right };
      }
    } else if (mainRef.current) {
      const main = buildMap(mainRef.current, VALENCIA, 9);
      floodLayer(predicted, "#EF4444").addTo(main);
      maps.current = { main };
    }
    return () => {
      Object.values(maps.current).forEach((m) => m && m.remove());
      maps.current = {};
    };
  }, [geo, predicted, isSplit]);

  const clearFocusMarkers = () => {
    focusMarkers.current.forEach(({ map, layer }) => { if (map.hasLayer(layer)) map.removeLayer(layer); });
    focusMarkers.current = [];
  };

  const handleFocus = (a) => {
    if (!a.coords) return;
    const targets = [maps.current.main, maps.current.left, maps.current.right].filter(Boolean);
    if (!targets.length) return;
    clearFocusMarkers();
    targets.forEach((m) => {
      m.flyTo(a.coords, 13, { duration: 1.1 });
      const marker = L.circleMarker(a.coords, {
        radius: 9, color: "#EF4444", weight: 2.5, fillColor: "#EF4444", fillOpacity: 0.45,
        className: "resilio-focus-pulse",
      }).addTo(m);
      marker
        .bindPopup(`<div class="resilio-popup"><b>${a.titulo || a.municipio || "—"}</b><br/>${a.municipio || ""}${a.return_period ? " · " + a.return_period : ""}</div>`)
        .openPopup();
      focusMarkers.current.push({ map: m, layer: marker });
    });
    setTimeout(clearFocusMarkers, 6000);
  };

  const info = state?.checkpoint === checkpoint ? state?.info?.[lang] || "" : "";
  const nasa = state?.nasa;
  const m = state?.metrics;
  const first = m?.first_critical;
  const cps = state?.checkpoints || CHECKPOINTS;

  return (
    <div className="flex-1 flex min-h-0" data-testid="dana-simulation">
      <div className="flex-1 flex flex-col min-h-0">
        <div
          data-testid="dana-banner"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500/15 border-b border-amber-500/40 text-amber-200 font-mono text-[11px] tracking-wide"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
          <span className="font-semibold">{t.dana_banner}</span>
        </div>

        {isSplit ? (
          <div className="flex-1 grid grid-cols-2 gap-px bg-slate-800 min-h-0" data-testid="dana-split-view">
            <div className="relative min-h-0">
              <div ref={leftRef} className="absolute inset-0" data-testid="dana-map-predicted" />
              <MapPanel label={t.dana_predicted} color="#EF4444" />
            </div>
            <div className="relative min-h-0">
              <div ref={rightRef} className="absolute inset-0" data-testid="dana-map-impact" />
              <MapPanel label={t.dana_real} color="#FB923C" />
            </div>
          </div>
        ) : (
          <div className="flex-1 relative min-h-0">
            <div ref={mainRef} className="absolute inset-0" data-testid="dana-map-main" />
            <MapPanel label={t.dana_predicted} color="#EF4444" />
          </div>
        )}

        <div className="border-t border-slate-800 bg-[#0B0F19] p-4 space-y-3 max-h-[62%] overflow-y-auto" data-testid="dana-timeline">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono tracking-wider uppercase text-slate-400">{t.dana_timeline}</span>
            <span className="text-[10px] font-mono text-slate-500">DANA · {state?.event_date || "2024-10-29"}</span>
          </div>
          {state?.chart && <LeadTimeChart data={state.chart} selected={checkpoint} onSelect={setCheckpoint} lang={lang} />}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            {cps.map((cp, i) => (
              <div key={cp} className="flex-1 min-w-[48px] flex items-center gap-1.5">
                <button
                  onClick={() => setCheckpoint(cp)}
                  data-testid={`dana-checkpoint-${cp}`}
                  className={`flex-1 px-2 py-2 rounded font-mono text-[11px] tracking-wider border transition-colors ${
                    checkpoint === cp
                      ? "bg-amber-500/20 border-amber-500/60 text-amber-200"
                      : "bg-[#111726] border-slate-700 text-slate-400 hover:border-amber-500/40 hover:text-amber-300"
                  }`}
                >
                  {cp}
                </button>
                {i < cps.length - 1 && <div className="h-px w-2 shrink-0 bg-slate-700" />}
              </div>
            ))}
          </div>
          <p className="text-[11px] leading-relaxed text-slate-300" data-testid="dana-info">{info}</p>

          <div className="flex flex-wrap items-center gap-2 font-mono text-[10px]">
            {m && (
              <span className="px-2 py-1 rounded bg-red-500/15 text-red-400" title={t.dana_variance} data-testid="dana-metric-variance">
                Δ +{m.variance_pct}% · {t.dana_variance}
              </span>
            )}
            {m && (
              <span className="px-2 py-1 rounded bg-cyan-500/15 text-cyan-300" data-testid="dana-metric-coverage">
                {t.dana_overlap} {m.overlap_pct}%
              </span>
            )}
            {m?.aemet_peak && (
              <span className="px-2 py-1 rounded bg-teal-500/15 text-teal-300" data-testid="dana-metric-aemet">
                {t.dana_aemet_peak}: {m.aemet_peak.mm} mm ({m.aemet_peak.station}) · est.
              </span>
            )}
            {first && (
              <span className="px-2 py-1 rounded bg-indigo-500/15 text-indigo-300" data-testid="dana-metric-lead">
                {t.dana_lead}: {first.checkpoint} · {first.lead_h} h
              </span>
            )}
            {nasa?.available ? (
              <span className="px-2 py-1 rounded bg-sky-500/15 text-sky-300 inline-flex items-center gap-1" title={t.dana_nasa}>
                <Droplets className="h-3 w-3" /> {t.dana_nasa}: {t.dana_peak} {nasa.peak_mm} mm ({nasa.peak_date})
              </span>
            ) : (
              <span className="px-2 py-1 rounded bg-slate-700/40 text-slate-400">{t.dana_nasa_na}</span>
            )}
          </div>

          {state?.focus && state.checkpoint === checkpoint && <FocusPanel f={state.focus} lang={lang} />}

          <div className="flex items-start gap-1.5 text-[10px] text-slate-500" data-testid="dana-inputs-note">
            <Cpu className="h-3 w-3 mt-0.5 shrink-0" />
            <span>{t.dana_inputs_note}</span>
          </div>

          {m?.sovereign && (
            <button
              type="button"
              onClick={() => downloadDanaReport(lang)}
              data-testid="dana-report-download"
              className="w-full inline-flex items-center justify-center gap-2 rounded border border-amber-500/50 bg-amber-500/15 px-3 py-2.5 text-[11px] font-mono tracking-wider text-amber-200 hover:bg-amber-500/25 transition-colors"
            >
              <Download className="h-4 w-4" /> {t.dana_report_btn}
            </button>
          )}
        </div>
      </div>

      {state ? (
        <AlertFeed alerts={state.alerts} summary={state.summary} onFocus={handleFocus} />
      ) : (
        <aside className="w-96 min-w-[360px] shrink-0 bg-[#0B0F19] border-l border-slate-800 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
        </aside>
      )}
    </div>
  );
};
